import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'
import {
  type AmountKind,
  evaluateAmountDraft,
  formatDraftLive,
  formatPlanningAmount,
  mapCaret,
  toUnits,
} from './planningAmount'

interface PlanningAmountInputProps {
  /** Сохранённое значение: ₽ с копейками или штуки. */
  value: number
  kind: AmountKind
  /** Подпись для экранных дикторов и тестов («Факт, ₽ — Иванов»). */
  label: string
  /** Только просмотр: ГД, своя строка РОПа, режимы «Квартал» / «Год» / «Период». */
  readonly?: boolean
  /** Подсказка при наведении на значение в режиме просмотра. */
  readonlyTitle?: string
  /** true — сохранено, false — сервер отказал (тост уже показан). */
  onSave: (next: number) => Promise<boolean>
}

/**
 * Поле «План» / «Факт» / АКБ в «Планировании». Разбор ввода — planningAmount.ts:
 * копейки через запятую или точку, «+50 000» / «−50 000» — прибавить / вычесть.
 * Сохраняет на выходе из поля и по Enter, Esc — вернуть сохранённое значение.
 * Непонятный ввод не сохраняется: поле подсвечивается и объясняет, что не так.
 */
export function PlanningAmountInput({
  value,
  kind,
  label,
  readonly = false,
  readonlyTitle,
  onSave,
}: PlanningAmountInputProps) {
  const saved = formatPlanningAmount(value, kind)
  const [draft, setDraft] = useState(saved)
  const [error, setError] = useState<string | null>(null)
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const caretRef = useRef<number | null>(null)
  const cancelRef = useRef(false)

  // Пришло новое сохранённое значение (своё сохранение, правка с другого места).
  useEffect(() => {
    setDraft(saved)
    setError(null)
  }, [saved])

  // Переформатирование при наборе двигает курсор в конец — возвращаем его.
  useLayoutEffect(() => {
    const el = inputRef.current
    if (caretRef.current === null || !el || document.activeElement !== el) return
    el.setSelectionRange(caretRef.current, caretRef.current)
    caretRef.current = null
  })

  if (readonly) {
    return (
      <span
        className="inline-block w-32 px-2 py-1 text-sm text-right tabular-nums text-gray-500"
        title={readonlyTitle}
      >
        {saved}
      </span>
    )
  }

  const evaluation = evaluateAmountDraft(draft, value, kind)
  const preview =
    focused && evaluation.ok && evaluation.relative
      ? `= ${formatPlanningAmount(evaluation.value, kind)}`
      : null

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const next = formatDraftLive(raw, kind)
    caretRef.current = mapCaret(raw, e.target.selectionStart ?? raw.length, next)
    setDraft(next)
    setError(null)
  }

  const commit = () => {
    setFocused(false)
    if (cancelRef.current) {
      cancelRef.current = false
      return
    }
    const result = evaluateAmountDraft(draft, value, kind)
    if (!result.ok) {
      setError(result.error)
      return
    }
    const sent = formatPlanningAmount(result.value, kind)
    setDraft(sent)
    if (toUnits(result.value, kind) === toUnits(value, kind)) return
    // Сервер отказал — возвращаем сохранённое значение, но только если
    // сотрудник не успел снова поправить поле, пока шёл запрос.
    void onSave(result.value).then((ok) => {
      if (!ok) setDraft((cur) => (cur === sent ? saved : cur))
    })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur()
    if (e.key === 'Escape') {
      cancelRef.current = true
      setDraft(saved)
      setError(null)
      e.currentTarget.blur()
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        inputMode={kind === 'money' ? 'decimal' : 'numeric'}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        value={draft}
        onChange={handleChange}
        onFocus={(e) => {
          setFocused(true)
          e.target.select()
        }}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className={`w-32 border rounded-lg px-2 py-1 text-sm text-right tabular-nums ${
          error ? 'border-red-400 bg-red-50' : 'border-gray-200'
        }`}
      />
      {error && (
        <div
          role="alert"
          className="mt-1 ml-auto max-w-[9rem] text-[10px] leading-tight text-red-600"
        >
          Не сохранено: {error}
        </div>
      )}
      {!error && preview && (
        <div className="mt-1 text-[10px] text-gray-500 whitespace-nowrap tabular-nums">
          {preview}
        </div>
      )}
    </>
  )
}
